import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useEffect, useMemo, useState } from "react";
import IconClose from "@/assets/icons/close.svg?react";
import FlameIcon from "@/assets/icons/streak-flame.svg?react";
import brokenCoin from "@/assets/images/streak-broken-coin.png";

interface StreakLostModalProps {
  isOpen: boolean;
  previousDayNumber: number;
  recoveryAvailableUntil: string | null;
  recoveryRequiresCheckIn?: boolean;
  onClose: () => void;
}

const PENDING_RECOVERY_KEY = "pending_streak_recovery";

const StreakLostModal = ({
  isOpen,
  previousDayNumber,
  recoveryAvailableUntil,
  recoveryRequiresCheckIn = false,
  onClose,
}: StreakLostModalProps) => {
  const navigate = useNavigate();
  const recoveryDeadlineMs = useMemo(() => {
    if (!recoveryAvailableUntil) return null;
    const value = new Date(recoveryAvailableUntil).getTime();
    return Number.isNaN(value) ? null : value;
  }, [recoveryAvailableUntil]);
  const [isExpired, setIsExpired] = useState(() => {
    return recoveryDeadlineMs !== null && recoveryDeadlineMs <= Date.now();
  });

  useEffect(() => {
    if (recoveryDeadlineMs === null) {
      setIsExpired(false);
      return;
    }
    const update = () => setIsExpired(recoveryDeadlineMs <= Date.now());
    update();
    const intervalId = window.setInterval(update, 1000);
    return () => window.clearInterval(intervalId);
  }, [recoveryDeadlineMs]);

  const handleBuyPremium = () => {
    if (isExpired) return;
    if (recoveryAvailableUntil) {
      try {
        localStorage.setItem(
          PENDING_RECOVERY_KEY,
          JSON.stringify({
            previous_day_number: previousDayNumber,
            recovery_available_until: recoveryAvailableUntil,
            recovery_requires_check_in: recoveryRequiresCheckIn,
          })
        );
      } catch {
        // localStorage mavjud emas (private mode/quota) — saqlamasdan davom etamiz
      }
    }
    onClose();
    navigate("/profile/premium");
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-[#0E0E14]/80"
        onClick={onClose}
      />

      <div className="relative w-[311px] rounded-[20px] border-2 border-white/08 bg-gray-linear py-4 flex flex-col items-center gap-5">
        <img
          src={brokenCoin}
          alt=""
          className="pointer-events-none select-none absolute left-1/2 -translate-x-1/2 -top-[70px] size-[140px]"
        />

        <div className="flex items-center justify-end w-[279px]">

          <button
            type="button"
            onClick={onClose}
            className="size-7 rounded-full bg-white/08 flex items-center justify-center active:opacity-70 transition-opacity"
            aria-label="Close"
          >
            <IconClose className="size-4 text-white" />
          </button>
        </div>
        <div className="flex flex-col items-center text-center gap-1 w-[263px]">
          <h2 className="text-[22px] font-bold text-white leading-130">
            Kunlik Seriya uzildi!
          </h2>
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className="flex items-baseline gap-1.5">
            <FlameIcon className="size-[41px] shrink-0" />
            <span className="text-[73px] font-extrabold text-white leading-none">
              {previousDayNumber}
            </span>
          </div>
          <p className="w-[263px] text-center text-sm font-normal text-white/80 leading-130">
            {recoveryRequiresCheckIn
              ? "Premium obuna bilan seriyani saqlab qoling. So'ng bugun DON olib, seriyani davom ettiring."
              : "Kunlik seriyangiz uzildi. Premium obuna bilan seriyani hoziroq tiklang va mukofotni saqlab qoling"}
          </p>
        </div>

        {isExpired ? (
          <p className="w-[263px] text-center text-sm font-semibold text-white/70 leading-130">
            Tiklash muddati tugagan
          </p>
        ) : (
          <button
            type="button"
            onClick={handleBuyPremium}
            className="w-[279px] h-11 rounded-xl border border-black bg-yellow-linear text-[#101017] text-sm font-semibold flex items-center justify-center active:opacity-90 transition-opacity"
          >
            {recoveryRequiresCheckIn
              ? "Premium bilan davom ettirish"
              : "Seriyani tiklash - Premium"}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StreakLostModal;
export { PENDING_RECOVERY_KEY };
